// In-sheet game picker — fetch upcoming games with best moneyline odds and
// let users add any side to the parlay slip without opening per-game panels.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Check, Search } from "lucide-react";
import { toast } from "sonner";
import { useParlay, MAX_LEGS, legKey } from "@/lib/parlay";
import PropsExpander from "@/components/PropsExpander";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = "#067647";

// Sports we offer in the picker — must match the backend's SPORT_KEY_MAP keys.
const PICKER_SPORTS = ["NFL", "NBA", "MLB", "NHL", "College Football", "Men's College Basketball", "MLS"];
// Sports that have props markets wired on the backend (defaults).
const PROPS_SPORTS = new Set(["NFL", "NBA", "MLB", "NHL", "College Football", "Men's College Basketball"]);

function decToAmerican(dec) {
  if (!dec || dec <= 1) return null;
  const v = dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
  return v > 0 ? `+${v}` : `${v}`;
}

function formatDay(commence) {
  if (!commence) return "TBD";
  try {
    const d = new Date(commence);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch { return commence.slice(0, 10); }
}

function formatTime(commence) {
  if (!commence) return "";
  try {
    const d = new Date(commence);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function GameAddButton({ side, team, dec, isActive, onPick }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={isActive}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-[10px] tracking-[0.1em] uppercase font-extrabold border-2 transition-colors focus-ring active:translate-y-px"
      style={{
        background: isActive ? ACCENT : "transparent",
        color: isActive ? "#fff" : "#111",
        borderColor: isActive ? ACCENT : "#111",
      }}
      title={isActive ? "Remove from parlay slip" : `Add ${team} moneyline`}
      data-testid={`picker-add-${side}`}
    >
      {isActive ? <Check size={11} strokeWidth={3} /> : <Plus size={11} strokeWidth={3} />}
      {team?.split(" ").pop()} {decToAmerican(dec)}
    </button>
  );
}

export default function PickerPanel() {
  const parlay = useParlay();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState([]); // empty = all

  async function fetchGames() {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(
        `${API}/betting/pickable-games?sports=${encodeURIComponent(PICKER_SPORTS.join(","))}`,
        { timeout: 30000 }
      );
      setGames(r.data?.games || []);
      setFetched(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not load games.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }

  // Auto-fetch on mount
  useEffect(() => { fetchGames(); /* eslint-disable-next-line */ }, []);

  const availableSports = useMemo(() => {
    const s = new Set();
    for (const g of games) s.add(g.sport);
    return Array.from(s);
  }, [games]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return games.filter((g) => {
      if (sportFilter.length > 0 && !sportFilter.includes(g.sport)) return false;
      if (q) {
        const hay = `${g.home_team} ${g.away_team} ${g.sport}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [games, search, sportFilter]);

  function toggleSport(s) {
    setSportFilter((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  }

  function pick(game, side) {
    const leg = {
      sport: game.sport,
      home_team: game.home_team,
      away_team: game.away_team,
      date: game.date || null,
      time: null,
      side,
    };
    const current = parlay.sideFor(leg);
    const atCap = parlay.legs.length >= MAX_LEGS && !current;
    if (atCap) {
      toast.error(`Parlay slip is full (max ${MAX_LEGS} legs).`);
      return;
    }
    if (current === side) {
      parlay.removeLeg(leg);
      toast(`Removed ${side === "home" ? game.home_team : game.away_team} from parlay slip.`);
    } else {
      parlay.addLeg(leg);
      toast.success(
        `Added ${side === "home" ? game.home_team : game.away_team} ML to parlay.`,
        { description: `${Math.min(parlay.legs.length + 1, MAX_LEGS)}/${MAX_LEGS} legs` }
      );
    }
  }

  return (
    <div className="border-2 border-[#111] bg-white mb-5" data-testid="parlay-picker-panel">
      <div className="px-3 py-2 border-b-2 border-[#111] bg-[#111] text-white flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase font-extrabold">
          Browse upcoming games
        </span>
        {fetched && !loading && (
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#aaa]">
            {games.length} with live odds
          </span>
        )}
      </div>

      <div className="p-3 space-y-3">
        {/* Search + sport filter */}
        <div className="flex items-center gap-2 border-2 border-[#111] px-2 bg-[#fafaf9]">
          <Search size={14} strokeWidth={2.4} className="text-[#555]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search team or sport…"
            className="flex-1 bg-transparent py-1.5 px-1 font-mono text-xs focus:outline-none"
            data-testid="picker-search-input"
          />
        </div>

        {availableSports.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid="picker-sport-chips">
            {availableSports.map((s) => {
              const active = sportFilter.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSport(s)}
                  aria-pressed={active}
                  data-testid={`picker-sport-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] uppercase font-extrabold border-2 transition-colors focus-ring"
                  style={{
                    background: active ? "#111" : "transparent",
                    color: active ? "#fff" : "#111",
                    borderColor: "#111",
                  }}
                >
                  {s}
                </button>
              );
            })}
            {sportFilter.length > 0 && (
              <button
                type="button"
                onClick={() => setSportFilter([])}
                className="px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] uppercase font-bold text-[#555] link-u"
                data-testid="picker-sport-clear"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Games list */}
        <div
          className="max-h-[320px] overflow-y-auto border-2 border-[#eee] divide-y divide-[#eee]"
          data-testid="picker-games-list"
        >
          {loading && (
            <div className="p-6 flex items-center justify-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold gap-2">
              <Loader2 size={14} className="spin-slow" />
              Loading odds…
            </div>
          )}
          {!loading && error && (
            <div className="p-4 font-mono text-xs text-[#c0272d] tracking-[0.05em] font-bold">
              {error}
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="p-6 text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#555]">
              {games.length === 0 ? "No upcoming games with live odds." : "No matches for your filter."}
            </div>
          )}
          {!loading && filtered.map((g) => {
            const legBase = {
              sport: g.sport, home_team: g.home_team, away_team: g.away_team,
              date: g.date || null, time: null,
            };
            const homeActive = legKey({ ...legBase, side: "home" }) === legKey({ ...legBase, side: parlay.sideFor(legBase) || "x" });
            const awayActive = legKey({ ...legBase, side: "away" }) === legKey({ ...legBase, side: parlay.sideFor(legBase) || "x" });
            const key = `${g.sport}|${g.commence_time}|${g.home_team}`;
            return (
              <div
                key={key}
                className="p-2.5 hover:bg-[#fafaf9]"
                data-testid={`picker-game-${key}`}
              >
                <div className="flex items-center gap-3 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#555] font-bold">
                      {g.sport} · {formatDay(g.commence_time)} {formatTime(g.commence_time)}
                    </div>
                    <div className="font-display font-extrabold text-sm text-[#111] leading-tight truncate">
                      {g.away_team} <span className="opacity-50 mx-1">@</span> {g.home_team}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <GameAddButton
                      side="away" team={g.away_team} dec={g.best_away?.decimal}
                      isActive={awayActive}
                      onPick={() => pick(g, "away")}
                    />
                    <GameAddButton
                      side="home" team={g.home_team} dec={g.best_home?.decimal}
                      isActive={homeActive}
                      onPick={() => pick(g, "home")}
                    />
                  </div>
                </div>
                {PROPS_SPORTS.has(g.sport) && (
                  <div className="mt-2">
                    <PropsExpander game={g} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
